import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Backdrop,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  FormHelperText,
  Dialog,
  DialogActions,
  InputAdornment,
  Tooltip,
  styled,
  tooltipClasses,
  ClickAwayListener,
  Table,
  DialogContent,
  DialogTitle,
  createTheme,
  Grid,
  Divider,
  IconButton,
  Popper,
  Box,
  Card,
  CardContent,
  Autocomplete
} from "@mui/material";
import { CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { XCircleIcon } from "@heroicons/react/24/outline";
import { ServiceApi } from "../../services/index.service";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CategoryCard from "./CategoryCard";
import { ContainerList, ProductionSheetFilters, ProductionTypeCategoryList } from "../../utils";
import "./ProductionSheet.css";
import AWS from "aws-sdk";
import ToggleableContent from "./ToggleableContent";
import { useSnackbar } from "notistack";
import { CloseTwoTone, Info, InfoRounded, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { ConfirmationPopup } from "./ConfirmationPopup";
import { orderBy } from "lodash";
//Tour Imports
import { continuousTour, updateSteps, runTour, showTourProgress } from "../../redux/userOnboarding/userOnboardingSlice";
import { VIEW_ONLY_PRODUCTION_SHEETS_TOUR_STEPS, PRODUCTION_SHEETS_TOUR_STEPS, PS_DUMMY_DATA, DashboardStepContent } from "../../utils/tourConstants";
import useIsTouchscreen from "../shared/useIsTouchscreen.hook";

export const ErrorTooltip = styled(({ className, ...props }) => (
  <Tooltip arrow {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.arrow}`]: { color: '#d32f2f' },
  [`& .${tooltipClasses.tooltip}`]: { backgroundColor: '#d32f2f', color: '#fff' },
}));

export const theme = createTheme({
  palette: {
    primary: {
      dark: "#B72C00",
      main: "#B72C00",
      light: "#FFF8FA"
    },
    secondary: {
      light: '#F3FFF4',
      main: '#006838',
      dark: '#006838'
    }
  },
});

const decimalOptions = [.125, .25, .375, .50, .625, .75, .875];
const fractions = { .125: "1/8", .25: "1/4", .375: "3/8", .50: "1/2", .625: "5/8", .75: "3/4", .875: "7/8" }

const getUpdatedDecimals = (decimalOpts, val) => {
  if (val?.toString()?.split('.').length > 1) {
    return decimalOpts.map(opt => `${val.toString()?.split('.')[0]}.${opt.toString()?.split('.')[1]}`);
  }
  return decimalOpts.map(opt => `${val || 0}.${opt.toString()?.split('.')[1]}`);
}

const decimalTooltipText = <span>When entering data in the Delivered, Return, Throw and Left column, you must utilize a decimal format.<br />
  Note: These are the only decimal increments the Production Sheet will accept. For example: 1.125 is acceptable, but 1.4 is not.</span>

export const NumberField = ({ name = "", label = "", value, disabled = false, error = "", onChange = () => { }, ...props }) => {
  const [tooltip, setTooltip] = useState(false);
  const [openSuggestion, setOpenSuggestion] = useState(false);
  const isTouchscreen = useIsTouchscreen();

  return (
    <FormControl
      sx={{ minWidth: 50, backgroundColor: "initial" }}
      size="small"
    >
      <Tooltip open={isTouchscreen && tooltip} title={name === 'leftOver' ? 'Left' : name}
        placement="left" arrow
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: '#e5e5e5',
              color: '#000000de',
              border: '1px solid #e0e0e0',
              textTransform: 'capitalize'
            },
          },
          arrow: { sx: { color: '#e5e5e5' } }
        }}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -14],
                },
              },
            ],
          },
        }}
      >
        <Autocomplete
          id={name}
          disableClearable
          disableListWrap
          blurOnSelect
          freeSolo
          open={openSuggestion}
          classes={{ inputRoot: "p-0" }}
          options={getUpdatedDecimals(decimalOptions, value)}
          renderOption={(props, option) => <p {...props} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 10px' }}>
            <span>{option}</span> <span>({Math.floor(value) > 0 ? Math.floor(value) : ''} {fractions[parseFloat(`.${option?.split('.')[1]}`)]})</span>
          </p>}
          slotProps={{ paper: { style: { width: 'max-content', minWidth: '110px' } }, popper: { placement: 'top' } }}
          ListboxProps={{ style: { padding: '5px 0' } }}
          value={value}
          onChange={(e, value) => onChange({ target: { value: value } }, name)}
          // onKeyDown={(e) => { if (e.key === '.' || e.key === 'Backspace') setOpenSuggestion(true); }}
          onFocus={() => setOpenSuggestion(true)}
          renderInput={(params) =>
            <TextField
              {...params}
              onWheel={(e) => e.target?.blur()}
              id={name}
              label={label}
              type="number"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                ...params.InputProps,
                className: error ? 'p-0 pr-1' : 'p-0',
                endAdornment: error && <InputAdornment className="ml-1" position="end"><ErrorTooltip color="error" title={error}><InfoRounded color="error" className="cursor-default" sx={{ fontSize: 16 }} /></ErrorTooltip></InputAdornment>
              }}
              inputProps={{ ...params.inputProps, className: 'pr-0' }}
              onChange={(e) => onChange(e, name)}
              error={!!error}
              onFocus={() => setTooltip(true)}
              onBlur={() => {
                setOpenSuggestion(false);
                setTooltip(false);
              }}
            />}
        />
      </Tooltip>
    </FormControl>

  )
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
function calculateSum(array, property) {
  const total = array.reduce((accumulator, object) => {
    return parseFloat(accumulator) + parseFloat(object[property]);
  }, 0);

  return total;
}

export const UserPermisions = {
  "Super Admin": { unlock: 'always', submit: false },
  "Admin": { unlock: 'always', submit: false },
  "Director(FC Lead)": { unlock: 'always', submit: false },
  "Franchise Consultant": { unlock: 168, submit: false },
  "Franchisee": { unlock: process.env.REACT_APP_UNLOCK_FRANCHISE_USER_TIME, submit: true },
  "Manager": { unlock: process.env.REACT_APP_UNLOCK_FRANCHISE_USER_TIME, submit: true },
  "Treat Team": { unlock: process.env.REACT_APP_UNLOCK_FRANCHISE_USER_TIME, submit: true },
}

export function checkForCorporateUser(role) {
  const adminRoles = ["Franchisee", "Manager", "Treat Team"]
  return adminRoles.includes(role)
}

const allowedTempPattern = /^(?<whole>999|\d{1,3})(?<fract>\.\d{0,1})?$/;

const ProductionSheet = ({ hasReportAccess, drawerOpen }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [flavorLoading, setFlavorLoading] = useState(false);
  const [storeList, setStoreList] = useState([]);
  const [query, setQuery] = useState("");
  const [store, setStore] = useState(null);
  const [date, setDate] = useState(new Date());
  const [openClearPSDataPopup, setClearDataConfirmation] = useState(false);
  // Network error states
  const [networkError, setNetworkError] = useState(false);
  const [dataFetchError, setDataFetchError] = useState(false);
  const defaultSheet = {
    dailySubmit: {
      bitIsVerified: false,
      comments: "",
      dateSelected: new Date(),
      eventDetails: "",
      intRitasStoreID: store?.intRitasStoreID,
      intStoreID: store?.intStoreID,
      intContactID: 0,
      intRoleID: 0,
      made: 0,
      netSales: 0,
      returnsPerContainer: 0,
      roleName: "",
      signature: "",
      sold: 0,
      start: 0,
      status: 1,
      throw: 0,
      total: 0,
      todayWeather: "",
      userName: "",
      afternoonTemp: null,
      afternoonWeather: "",
      eveningTemp: null,
      eveningWeather: "",
      storeOpeningTemp: null,
      storeOpeningWeather: "",
      storeClosingTemp: null,
      storeClosingWeather: ""
    },
    production: [],
  };
  const [productionSheetData, setProductionSheetData] = useState(defaultSheet);
  const defaultItem = {
    containerID: 0,
    delivered: 0,
    dailySubmitID: 0,

    flavorID: 0,
    intProductTypeID: 0,
    intFlavorID: 0,
    leftOver: 0,
    made: 0,
    quantity: 1,
    returned: 0,
    sold: 0,
    start: 0,
    throw: 0,
    total: 0,

  };
  const s3 = new AWS.S3();
  const [open, setOpen] = React.useState(false);
  const [productionItem, setProductionItem] = useState(defaultItem);
  const [categories, setCategories] = useState([]);
  const [flavorsList, setFlavorsList] = useState([]);
  const [allFlavorsList, setAllFlavorsList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const serviceapi = new ServiceApi();
  const data = useSelector((state) => state);
  const [selectedImage, setSelectedImage] = React.useState("");

  const { storage, userOnboarding } = useSelector((state) => state);

  const [formErrors, setFormErrors] = useState({});
  const { enqueueSnackbar } = useSnackbar();
  const [draftMessage, setDraftMessage] = useState(false)
  const [filterBy, setFilterBy] = useState('recent');
  const [searchText, setSearchText] = useState('');
  const [showBanner, setShowBanner] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editableCatogery, setEditableCatogery] = useState('');
  const [sortBy, setSortBy] = useState('alpha-asc');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, selectedDate: '' });
  const [toggleFormOpen, setToggleFormOpen] = useState(false)
  const [width, setWindowWidth] = useState(0);
  const breakPointWidth = 767;

  const [currentWeather, setCurrentWeather] = useState();
  const [fullDayWeather, setFullDayWeather] = useState([]);
  const [afternoonWeatherList, setAfternoonWeatherList] = useState({ temperatures: [], conditions: [] });
  const [eveningWeatherList, setEveningWeatherList] = useState({ temperatures: [], conditions: [] });
  const [bulkEdit, setBulkEdit] = useState(false);

  useEffect(() => {
    if (fullDayWeather?.length > 0) {
      const uniqAfternoonPositions = [12, 13, 14, 15, 16, 17].map(index => fullDayWeather[index])?.filter(i => i !== undefined);;
      const uniqEveningPositions = [18, 19, 20, 21, 22].map(index => fullDayWeather[index])?.filter(i => i !== undefined);;
      setAfternoonWeatherList({ temperatures: [...new Set(uniqAfternoonPositions.map(obj => parseInt(obj?.temp_f)))], conditions: [...new Set(uniqAfternoonPositions.map(obj => obj?.condition?.text))] });
      setEveningWeatherList({ temperatures: [...new Set(uniqEveningPositions.map(obj => parseInt(obj?.temp_f)))], conditions: [...new Set(uniqEveningPositions.map(obj => obj?.condition?.text))] });
      const storeOpeningTemp = uniqAfternoonPositions?.shift();
      const storeClosingTemp = uniqEveningPositions?.pop();
      setProductionSheetData({
        ...productionSheetData,
        dailySubmit: {
          ...productionSheetData?.dailySubmit,
          storeOpeningTemp: storeOpeningTemp?.temp_f,
          storeOpeningWeather: storeOpeningTemp?.condition?.text,
          storeClosingTemp: storeClosingTemp?.temp_f,
          storeClosingWeather: storeClosingTemp?.condition?.text
        },
      });
    }
    else {
      setAfternoonWeatherList({ temperatures: [], conditions: [] });
      setEveningWeatherList({ temperatures: [], conditions: [] });
      setProductionSheetData({
        ...productionSheetData,
        dailySubmit: {
          ...productionSheetData?.dailySubmit,
          storeOpeningTemp: '',
          storeOpeningWeather: '',
          storeClosingTemp: '',
          storeClosingWeather: ''
        },
      });
    }
  }, [fullDayWeather])

  useEffect(() => {
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  const sortProductionData = (production = []) => {
    if (production?.length === 0) return production;
    let sortedProductionData = [];
    if (sortBy === 'alpha-asc' || sortBy === 'alpha-desc') {
      sortedProductionData = orderBy([...production], ['intProductTypeID', 'flavor.flavor_name'], ['asc', sortBy === 'alpha-asc' ? 'asc' : 'desc']);
    }
    else if (sortBy === 'recent') {
      sortedProductionData = orderBy([...production], ['intProductTypeID', 'modifiedAt'], 'asc');
    }
    else sortedProductionData = [...production];
    return sortedProductionData;
  };

  useEffect(() => {
    if (sortBy)
      setProductionSheetData({ ...productionSheetData, production: sortProductionData(productionSheetData?.production) });
  }, [sortBy]);

  const updateDimensions = () => {
    const width = window.innerWidth;
    setWindowWidth(width);
  };

  useEffect(() => {
    getAllFlavors();
  }, [store, filterBy]);

  const getContainerValue = (containerID) => {
    const ContainerValue = ContainerList?.filter(i => i?.id === containerID)[0]?.value
    return ContainerValue
  }

  // useEffect(() => {
  //   if (store?.intStoreID && date)
  //     getProductionSheet({ intStoreID: store?.intStoreID, dateSelected: dayjs(date).format("YYYY-MM-DD") }, true);
  // }, [data?.userOnboarding?.psTourSkipped, data?.userOnboarding?.psTourCompleted])

  useEffect(() => {
    setStore(storage?.store);
  }, [storage?.store]);

  useEffect(() => {
    const newMade = (getContainerValue(productionItem.containerID || 0) * parseFloat(productionItem.quantity || 0)) || 0;
    const newTotal =
      parseFloat(productionItem.start || 0) +
      parseFloat(newMade || 0) +
      parseFloat(productionItem.returned || 0) -
      parseFloat(productionItem.delivered || 0) -
      parseFloat(productionItem.throw || 0);
    const newSold = parseFloat(newTotal || 0) - parseFloat(productionItem.leftOver || 0);
    setProductionItem({ ...productionItem, total: newTotal, sold: newSold, made: newMade })
  }, [
    productionItem.containerID,
    productionItem.quantity,
    productionItem.start,
    productionItem.returned,
    productionItem.delivered,
    productionItem.throw,
    productionItem.leftOver,
  ]);

  const getWeatherInfo = async (type = 'history') => {
    const req = {
      key: "65d3fabcfa3b4d3db77194102232111",
      geo: (store?.Latitude && store?.Longitude) ? `${store?.Latitude},${store?.Longitude}` : store?.ZipCode,
      dt: dayjs(date).format("YYYY-MM-DD"),
      hr: '1,2'
    };
    if (req.geo) {
      try {
        setFlavorLoading(true);
        if (type === 'history') {
          const response = await serviceapi?.fetchWeatherHistory(req);
          if (response?.status === 200) {
            const newRes = response.data.forecast?.forecastday[0]?.hour;
            setFullDayWeather(newRes);
          }
        }
        else if (type === 'current') {
          const response = await serviceapi?.fetchCurrentWeatherInfo(req);
          if (response?.status === 200)
            setCurrentWeather(response?.data?.current);
        }
      } catch (e) {
        console.log(e);
      } finally {
        setFlavorLoading(false);
      }
    }
    // }
  }

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
  };

  const getSearchResults = (productionData, searchKey) => {
    let production = JSON.parse(JSON.stringify(productionData));
    production = production.map(obj => ({ ...obj, flavorsList: obj.flavorsList?.filter(f => (f.flavor?.flavor_name)?.toLowerCase().includes(searchKey.toLowerCase())) }));
    return production;
  }

  const handleEdit = async (dataItem) => {
    setDraftMessage(true);
    await getCategoryFlavors(dataItem?.intProductTypeID);
    setProductionItem({
      ...dataItem,
      intFlavorID: dataItem?.flavor?.intFlavorID,
    });
  };

  const handleUpdate = (rowIndex, rowData) => {
    setDraftMessage(true);
    let { production } = JSON.parse(JSON.stringify(productionSheetData));
    const flavorIndex = production?.findIndex(obj => (obj.flavorID === rowData?.flavorID) && (obj.intProductTypeID === rowData.intProductTypeID) && !obj.isDelete);
    production[flavorIndex] = rowData;
    setProductionSheetData({ ...productionSheetData, production });
  }

  const handleBulkUpdate = (data) => {
    setDraftMessage(true);
    let { production } = JSON.parse(JSON.stringify(productionSheetData));
    data.forEach(rowData => {
      const flavorIndex = production?.findIndex(obj => (obj.flavorID === rowData?.flavorID) && (obj.intProductTypeID === rowData.intProductTypeID));
      production[flavorIndex] = rowData;
    });
    setProductionSheetData({ ...productionSheetData, production: production });
  }

  const handleDelete = (dataItem) => {
    setDraftMessage(true)
    const mainData = JSON.parse(JSON.stringify(productionSheetData));
    let sheetData = mainData.production;
    const findIndex = sheetData?.findIndex(
      (i) =>
        (i?.flavor?.flavorID === dataItem?.flavor?.flavorID) &&
        (i?.containerID === dataItem?.containerID) &&
        (i?.intProductTypeID === dataItem?.intProductTypeID)
    );
    if (findIndex !== -1) {
      const val = sheetData[findIndex];
      if (val?.id) {
        if (val?.start > 0) {
          sheetData[findIndex].isUpdate = true;
          sheetData[findIndex].quantity = 0;
          sheetData[findIndex].made = 0;
          sheetData[findIndex].delivered = 0;
          sheetData[findIndex].returned = 0;
          sheetData[findIndex].throw = 0;
          sheetData[findIndex].total = val?.start;
          sheetData[findIndex].leftOver = 0;
          sheetData[findIndex].sold = val?.start;
          sheetData[findIndex].containers = "[]"
        }
        else
          sheetData[findIndex].isDelete = true;
      } else {
        sheetData.splice(findIndex, 1);
      }
    }
    setProductionSheetData({ ...productionSheetData, production: sheetData });
  };

  const changeDate = (newValue) => {
    if (draftMessage) {
      openConfirmationPopup(newValue)
    } else {
      changeDateConfirm(newValue)
    }
  };

  const changeDateConfirm = (newValue) => {
    setDate(newValue);
    formChange(dayjs(newValue).format("YYYY-MM-DD"), "dateSelected");
    setFormErrors({});
  }

  useEffect(() => {
    if (store && date && data?.profile?.role && userOnboarding?.fetchedTourStatus) {
      getProductionSheet({ intStoreID: store?.intStoreID, dateSelected: dayjs(date).format("YYYY-MM-DD") }, true);
      setTimeout(() => {
        handleScroll('top');
      }, 1000);
    }
  }, [store, date, data?.profile?.role, userOnboarding?.tourStatus?.productionSheets, userOnboarding?.fetchedTourStatus]);

  // Code to fetch Current Weather information from WeatherAPI
  // useEffect(() => {
  //   if (store)
  //     getWeatherInfo('current');
  // }, [store]);

  useEffect(() => {
    if (productionSheetData?.production) {
      summaryCalculation(productionSheetData?.production);
    }
  }, [productionSheetData.production]);

  const startProductionSheetsTour = () => {
    let tour_steps = []
    if (checkForCorporateUser(data?.profile?.role)) {
      //Steps for TManager, Tfranchise, TTMember
      tour_steps = JSON.parse(JSON.stringify(PRODUCTION_SHEETS_TOUR_STEPS));
      // Setting return to dashboard html to last step content as parsing is not working for html properties
      tour_steps[tour_steps.length - 1].content = DashboardStepContent;
      //Show Unlock Button at last step if dailySubmit status is 3
      if (productionSheetData?.dailySubmit?.status == 3) {
        tour_steps = tour_steps.filter(step => !step.disabledWhenLocked);
      }
      else tour_steps = tour_steps.filter(step => !step.disabledWhenUnlocked);
    } else {
      tour_steps = VIEW_ONLY_PRODUCTION_SHEETS_TOUR_STEPS;
    }
    dispatch(updateSteps(tour_steps));
    dispatch(continuousTour());
    dispatch(showTourProgress());
    dispatch(runTour("productionSheets"));
  }

  const openConfirmationPopup = (newValue) => {
    setConfirmDialog({ open: true, selectedDate: newValue });
    // setDate(newValue);
  }

  const getProductionSheet = async (getSheetObj, showBanner = false) => {
    try {
      setLoading(true);
      const response = await serviceapi.getProductionSheetData(getSheetObj);
      if (response?.dailySubmit?.intStoreID) {
        let productionData = { ...response, production: sortProductionData(response?.production) };
        //Add Dummy Data if production data not available for tour purpose
        if (!userOnboarding?.tourStatus?.productionSheets) {
          if (response?.production.length == 0) {
            productionData = PS_DUMMY_DATA
            startProductionSheetsTour();
          }
          else startProductionSheetsTour();
        }
        setProductionSheetData(productionData);
        if (showBanner && checkForCorporateUser(data?.profile?.role) && response?.production?.length > 0) {
          setShowBanner(true);
          setTimeout(() => {
            setShowBanner(false);
          }, 5000);
        }

      } else {
        setProductionSheetData({
          ...defaultSheet,
          dailySubmit: {
            ...defaultSheet?.dailySubmit,
            ["dateSelected"]: getSheetObj.dateSelected
          },
        });
      }
      getWeatherInfo('history');
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.log(e);
    }
    setBulkEdit(false);
    setIsEditing(false);
  };

  const summaryCalculation = (dataRecords) => {
    const data = [...dataRecords];
    const flavorsList = data?.filter(flavor => !flavor.isDelete);
    const dailyValue = {
      ...productionSheetData?.dailySubmit,
      start: calculateSum(flavorsList, "start"),
      made: calculateSum(flavorsList, "made"),
      delivered: calculateSum(flavorsList, "delivered"),
      returned: calculateSum(flavorsList, "returned"),
      throw: calculateSum(flavorsList, "throw"),
      leftOver: calculateSum(flavorsList, "leftOver"),
      sold: calculateSum(flavorsList, "sold"),
      total: calculateSum(flavorsList, "total"),
      dateSelected: dayjs(date).format("YYYY-MM-DD"),
      intRitasStoreID: store?.intRitasStoreID,
      intStoreID: store?.intStoreID,
    };
    setProductionSheetData({
      ...productionSheetData,
      production: dataRecords,
      dailySubmit: dailyValue,
    });
  };

  const getProductionSheetCategories = async () => {
    const response = await serviceapi?.fetchProductionSheetCategories();
    setCategories(response);
  };

  // Function to find objects with a specific key and value
  function filterByProductTypeID(intProductTypeID) {
    return allFlavorsList.filter(
      (item) => item.intProductTypeID === intProductTypeID
    );
  }

  const getAllFlavors = async () => {
    if (store?.intStoreID) {
      let req = {
        keyword: "",
        productID: "",
      };
      try {
        setFlavorLoading(true);
        const response = await serviceapi?.fetchProductionSheetFlavors(store?.intStoreID, filterBy);
        setAllFlavorsList(response);
      } catch (e) {
        console.log(e);
      } finally {
        setFlavorLoading(false);
      }
    }
  };

  const getCategoryFlavors = async (categoryID) => {
    try {
      setFlavorLoading(true);
      const result = filterByProductTypeID(categoryID);
      setFlavorsList(result);
      setFlavorLoading(false);
    } catch (e) {
      setFlavorLoading(false);
      console.log(e);
    }
  };

  const handleChange = async (value, name) => {
    if (name === "intProductTypeID") {
      await getCategoryFlavors(value);
      setProductionItem({ ...productionItem, [name]: value, intFlavorID: 0, containerID: 0 });
    } else if (name === "intFlavorID") {
      setProductionItem({ ...productionItem, [name]: value, containerID: 0 });
    } else {
      setProductionItem({ ...productionItem, [name]: value });
    }
    // Clear the error message for the field when it's changed
    setFormErrors({
      ...formErrors,
      [name]: "",
    });
  };

  const formChange = async (value, name) => {
    setProductionSheetData({
      ...productionSheetData,
      dailySubmit: {
        ...productionSheetData?.dailySubmit,
        [name]: value,
      },
    });
    if ((name === "afternoonTemp" || name === "eveningTemp") && (!allowedTempPattern.test(value))) {
      setFormErrors({
        ...formErrors,
        [name]: "Please enter a valid temperature",
      });
    }
    else {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
    // setToggleFormOpen(true)
  };

  // console.log(productionSheetData);
  const generateNewRecord = (isCategoryExits = true) => {
    const flavorRecord = flavorsList?.find((i) => i.intFlavorID === productionItem?.intFlavorID);
    const flavorObj = {
      flavorID: flavorRecord.intFlavorID,
      flavor: flavorRecord.flavor_name,
      flavor_name: flavorRecord.flavor_name,
      image_url: flavorRecord.imageURL,
      quarter_container_recipe: flavorRecord.quarterContainer,
      half_container_recipe: flavorRecord.halfContainer,
      single_container_recipe: flavorRecord.singleContainer,
      double_container_recipe: flavorRecord.doubleContainer,
    };
    const {
      start,
      containerID,
      intProductTypeID,
      quantity,
      made,
      delivered,
      returned,
      leftOver,
      sold,
      total,
    } = productionItem;
    const obj = {
      flavor: flavorObj,
      flavorID: flavorObj.flavorID,
      // intFlavorID: flavorRecord.intFlavorID,
      containers: JSON.stringify([{ id: containerID, date: new Date().toISOString() }]),
      containerID,
      intProductTypeID: intProductTypeID,
      quantity,
      start,
      made,
      delivered,
      returned,
      throw: productionItem?.throw,
      leftOver,
      sold,
      total,
    };
    if (isCategoryExits) {
      return obj;
    } else {
      return {
        intProductTypeID,
        productTypeName: flavorRecord?.product_type_name,
        flavorsList: [obj],
      };
    }
  };

  const calculateValues = (row) => {
    const newTotal = parseFloat(row?.start || 0) + parseFloat(row?.made || 0) +
      parseFloat(row?.returned || 0) -
      parseFloat(row?.delivered || 0) -
      parseFloat(row?.throw || 0);
    const newSold = parseFloat(newTotal || 0) - parseFloat(row?.leftOver || 0);
    return { total: newTotal, sold: newSold };
  }

  const handleAdd = (e) => {
    e.preventDefault();
    setDraftMessage(true)
    const errors = {};
    if (!productionItem.intProductTypeID) {
      errors.intProductTypeID = "Please select the Category.";
    }
    if (!productionItem.containerID) {
      errors.containerID = "Please select a container.";
    }
    if (!productionItem.intFlavorID) {
      errors.intFlavorID = "Please select a flavor.";
    }
    setFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      let productionData = JSON.parse(JSON.stringify(productionSheetData.production));
      const flavorIndex = productionData?.findIndex(obj => (obj.flavorID === productionItem?.intFlavorID && obj.intProductTypeID === productionItem?.intProductTypeID));
      if (flavorIndex === -1) productionData?.push(generateNewRecord(true));
      else {
        if (productionData[flavorIndex]?.isDelete) {
          productionData[flavorIndex] = { ...generateNewRecord(true), isUpdate: true, id: productionData[flavorIndex]?.id };
        }
        else {
          productionData[flavorIndex].containers = JSON.stringify([...(JSON.parse(productionData[flavorIndex]?.containers)), { id: productionItem.containerID, date: new Date().toISOString() }]);
          productionData[flavorIndex].made += getContainerValue(productionItem.containerID);
          const { total, sold } = calculateValues(productionData[flavorIndex]);
          productionData[flavorIndex].total = total;
          productionData[flavorIndex].sold = sold;
          productionData[flavorIndex].isUpdate = true;
          enqueueSnackbar("Batch added Successfully", {
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "success",
          });
        }
      }
      setProductionSheetData({ ...productionSheetData, production: sortProductionData(productionData) });
      setProductionItem(prevState => ({ ...defaultItem, intProductTypeID: prevState.intProductTypeID }));
    }
  };

  const handleAddOld = (e) => {
    setDraftMessage(true)
    e.preventDefault();
    const errors = {};
    if (productionItem.intProductTypeID === "") {
      errors.intProductTypeID = "Please enter a intProductTypeID.";
    }
    if (productionItem.containerID === "") {
      errors.containerID = "Please enter a containerID.";
    }
    if (productionItem.intFlavorID === "") {
      errors.intFlavorID = "Please enter a flavorID.";
    }
    if (productionItem.quantity < 0) {
      errors.quantity = "Please enter a quantity.";
    }
    if (productionItem.start < 0) {
      errors.start = "Please enter a start.";
    }
    // if (productionItem.made < 0) {
    //   errors.made = "Please enter a made.";
    // }
    if (productionItem.delivered < 0) {
      errors.delivered = "Please enter a delivered.";
    }
    if (productionItem.returned < 0) {
      errors.returned = "Please enter a returned.";
    }
    if (productionItem.throw < 0) {
      errors.throw = "Please enter a throw.";
    }
    // if (productionItem.leftOver < 0) {
    //   errors.leftOver = "Please enter a leftOver.";
    // }
    // if (productionItem.total < 0) {
    //   errors.total = "Please enter a total.";
    // }
    // if (productionItem.sold < 0) {
    //   errors.sold = "Please enter a sold.";
    // }
    setFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      const mainData = { ...productionSheetData };
      let sheetData = mainData.production;
      const findIndexValue = mainData.production?.findIndex(
        (j) => j?.intProductTypeID === productionItem?.intProductTypeID
      );
      if (findIndexValue === -1) {
        const item = generateNewRecord(false);
        console.log(item);
        sheetData.push(item);
      } else {
        sheetData = mainData.production?.map((dataItem) => {
          if (dataItem?.intProductTypeID === productionItem?.intProductTypeID) {
            let flavorsData = [...dataItem?.flavorsList];
            const findIndex = flavorsData?.findIndex(
              (i) =>
                i?.flavor?.flavorID === productionItem?.flavorID &&
                i?.containerID === productionItem?.containerID
            );
            if (findIndex === -1) {
              const val = generateNewRecord();
              flavorsData.push(val);
            } else {
              flavorsData = flavorsData?.map((flvItem) => {
                if (
                  flvItem?.flavor?.intFlavorID ===
                  productionItem?.intFlavorID &&
                  flvItem?.containerID === productionItem?.containerID
                ) {
                  return { ...flvItem, ...productionItem };
                } else {
                  return { ...flvItem };
                }
              });
            }
            return { ...dataItem, flavorsList: flavorsData };
          } else {
            return { ...dataItem };
          }
        });
      }
      setProductionSheetData({ ...productionSheetData, production: [...sheetData] });
      setProductionItem(defaultItem);
    }
  };

  const handleValdiation = (action) => {
    const productionSheet = { ...productionSheetData };
    const errors = { ...formErrors };
    let check = true
    if (!productionSheet.dailySubmit.userName) {
      errors.userName = "Please enter the user name";
      check = false
      setToggleFormOpen(true)
    }
    if (action === 'submit') {
      if (!productionSheet.dailySubmit.afternoonTemp || !allowedTempPattern.test(productionSheet.dailySubmit.afternoonTemp)) {
        errors.afternoonTemp = "Please enter a valid temperature";
        check = false
        setToggleFormOpen(true)
      }
      if (!productionSheet.dailySubmit.afternoonWeather) {
        errors.afternoonWeather = "Please select a valid option";
        check = false
        setToggleFormOpen(true)
      }
      if (!productionSheet.dailySubmit.eveningTemp || !allowedTempPattern.test(productionSheet.dailySubmit.eveningTemp)) {
        errors.eveningTemp = "Please enter a valid temperature";
        check = false
        setToggleFormOpen(true)
      }
      if (!productionSheet.dailySubmit.eveningWeather) {
        errors.eveningWeather = "Please select a valid option";
        check = false
        setToggleFormOpen(true)
      }
    }
    if (action === 'submit' && !productionSheet.dailySubmit.bitIsVerified) {
      errors.bitIsVerified = "Please select the checkbox";
      check = false
      setToggleFormOpen(true)
    }
    else {
      errors.bitIsVerified = "";
    }
    setFormErrors(errors)
    return check
  }
  const removeProductionData = async () => {
    const dailysubmit_id = productionSheetData?.dailySubmit?.id
    const req = {
      dailysubmit_id
    }
    setFlavorLoading(true);
    const response = await serviceapi?.deleteproductionsheet(
      req
    );
    setFlavorLoading(false);
    if (response?.statusCode && response?.statusCode === 200) {
      enqueueSnackbar("Production Sheet Cleared Successfully", {
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
        variant: "success",
      });
      const getSheetObj = {
        intStoreID: productionSheetData?.intStoreID | store?.intStoreID,
        dateSelected: productionSheetData?.dailySubmit?.dateSelected,
      };
      await getProductionSheet(getSheetObj);

    } else {
      enqueueSnackbar("Something went wrong please try again", {
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
        variant: "error",
      });
    }

  }

  const handleClearDataConfirmation = () => {
    removeProductionData();
    setClearDataConfirmation(false);
  }

  const handleClearDataCancellation = () => {
    setClearDataConfirmation(false);
  }
  const submitProductionData = async (action) => {
    const check = handleValdiation(action)
    // console.log("submitProductionData", productionSheetData);
    setDraftMessage(false)
    if (check) {
      try {
        setFlavorLoading(true);
        if (productionSheetData?.dailySubmit?.status === 2 || productionSheetData?.dailySubmit?.status === 4) {
          let statusUpdated = { ...productionSheetData };
          if (action === 'submit') {
            statusUpdated.dailySubmit.status = 3;
          } else if (action === 'save') {
            statusUpdated.dailySubmit.status = 2;
          }
          statusUpdated.dailySubmit.userID = data?.profile?.uid;
          const response = await serviceapi?.updateproductionsheet(
            statusUpdated
          );
          if (response?.statusCode && response?.statusCode === 200) {
            setDraftMessage(false)
            const getSheetObj = {
              intStoreID: productionSheetData?.intStoreID | store?.intStoreID,
              dateSelected: productionSheetData?.dailySubmit?.dateSelected,
            };
            getProductionSheet(getSheetObj);
            enqueueSnackbar(action === 'submit' ? "Submitted Successfully" : "Saved Successfully", {
              anchorOrigin: {
                vertical: "top",
                horizontal: "right",
              },
              variant: "success",
            });
            // console.log("submit response", response);
            setFlavorLoading(false);
            setSearchText('');
          } else {
            let statusUpdated = { ...productionSheetData };
            statusUpdated.dailySubmit.status = 2;
            enqueueSnackbar(`Data error while ${action === 'submit' ? 'submitting' : 'saving'}`, {
              anchorOrigin: {
                vertical: "top",
                horizontal: "right",
              },
              variant: "error",
            });
            setFlavorLoading(false);
          }
        } else {
          let statusUpdated = { 
            ...productionSheetData,
            dailySubmit: {
              ...productionSheetData.dailySubmit,
              status: action === 'submit' ? 3 : 2
            }
          };
          
          let response; // Declare response variable outside try block
          
          try {
            response = await serviceapi?.submitProductionSheetData(statusUpdated);
          } catch (e) {
            let statusUpdated = {
              ...productionSheetData,
              dailySubmit: {
                ...productionSheetData.dailySubmit,
                status: 1
              }
            };
            setProductionSheetData(statusUpdated);
            throw e; // Re-throw to be caught by outer catch block
          }
          
          if (response?.statusCode && response?.statusCode === 200) {
            setDraftMessage(false)
            const getSheetObj = {
              intStoreID: productionSheetData?.intStoreID | store?.intStoreID,
              dateSelected: productionSheetData?.dailySubmit?.dateSelected,
            };
            getProductionSheet(getSheetObj);
            enqueueSnackbar(action === 'submit' ? "Submitted Successfully" : "Saved Successfully", {
              anchorOrigin: {
                vertical: "top",
                horizontal: "right",
              },
              variant: "success",
            });
            setFlavorLoading(false);
            setSearchText('');
          } else {
            let statusUpdated = {
              ...productionSheetData,
              dailySubmit: {
                ...productionSheetData.dailySubmit,
                status: 1
              }
            };
            setProductionSheetData(statusUpdated);
            console.log("Error in submit response", response);
            console.error(statusUpdated);
            enqueueSnackbar(`Data error while ${action === 'submit' ? 'submitting' : 'saving'}`, {
              anchorOrigin: {
                vertical: "top",
                horizontal: "right",
              },
              variant: "error",
            });
            setFlavorLoading(false);
          }
        }
      } catch (e) {
        console.error("Error submitting production data:", e);
        setFlavorLoading(false);
        
        try {
          // Safely extract error message
          const errorMsg = String(e?.message || e || 'Unknown error');
          
          // Check if the error is related to network connectivity
          const isNetworkError = !navigator.onLine || 
                                 e?.code === 'ERR_NETWORK' || 
                                 errorMsg.includes('Failed to fetch') || 
                                 errorMsg.includes('Network') || 
                                 errorMsg.includes('ECONNABORTED') ||
                                 e?.request === undefined ||
                                 (e?.response === undefined && e?.request !== undefined);
          
          if (isNetworkError) {
            setNetworkError(true);
            enqueueSnackbar("Network connection lost. Please check your internet connection and try again.", {
              anchorOrigin: {
                vertical: "top",
                horizontal: "right",
              },
              variant: "error",
              autoHideDuration: 6000
            });
          } else if (e?.response?.status) {
            // Handle HTTP errors with response
            enqueueSnackbar(`Server error: ${e.response.status}. Please try again.`, {
              anchorOrigin: {
                vertical: "top",
                horizontal: "right",
              },
              variant: "error",
            });
          } else {
            // Handle other types of errors
            enqueueSnackbar(`Error: ${errorMsg}`, {
              anchorOrigin: {
                vertical: "top",
                horizontal: "right",
              },
              variant: "error",
            });
          }
        } catch (handlerError) {
          console.error('Error in error handler:', handlerError);
          enqueueSnackbar("Something went wrong while submitting the data. Please try again.", {
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "error",
          });
        }
      }
    }
  };
  const showImagePDF = (val) => {
    if (val && val !== "NA") {
      setSelectedImage(getSignedUrl(val));
      setOpen(true);
    } else {
      return;
    }
  };
  const getSignedUrl = (file) => {
    const imgPath = file.split("/");
    const iParams = {
      Bucket: process.env.REACT_APP_AWS_BUCKET,
      Key: `${imgPath[3]}/${imgPath[4]}`,
    };
    var url = s3.getSignedUrl("getObject", iParams);
    return file;
  };

  const checkForResubmit = () => {
    if (data?.profile?.role === "Super Admin" ||
      data?.profile?.role === "Admin" ||
      data?.profile?.role === "Director(FC Lead)" ||
      data?.profile?.role === "Franchise Consultant") {
      if ((productionSheetData?.dailySubmit?.status === 3)) {
        return {
          buttonValue: "Re-Submit",
          classValue: true,
        };
      } else {
        return {
          buttonValue: "Submit",
          classValue: false,
        };
      }
    } else {
      // const currentDate = dayjs();
      // const givenDate = dayjs(productionSheetData?.dailySubmit?.dateSelected)
      // const timeDifferenceInDays = givenDate.diff(currentDate, 'day');
      // if (timeDifferenceInDays >= -1) {
      if (productionSheetData?.dailySubmit?.status === 3) {
        return {
          buttonValue: "Re-Submit",
          classValue: true,
        };
      } else {
        return {
          buttonValue: "Submit",
          classValue: false,
        };
      }
      // } else {
      //   return {
      //     buttonValue: "Submit",
      //     classValue: true,
      //   };
      // }
    }
  };
  const handleClose = () => {
    setOpen(false);
  };
  const imageOnLoad = (event) => {
    console.log(`Picture successfully ${event.currentTarget.src} loaded.`);
    if (event.currentTarget.className !== "error") {
      event.currentTarget.className = "success";
    }
  };
  const imageOnError = (event) => {
    event.currentTarget.src = "../../assets/images/noimage.jpg";
    event.currentTarget.className = "error";
  };

  const handleInputChange = (e, name) => {
    const value = parseFloat(e.target.value || 0);
    if (value < 0) return null;
    // setInputValue(value);
    setProductionItem({ ...productionItem, [name]: value });
    // Regular expression to match a float/double with a multiple of 25 after the decimal point
    const pattern = /^\d*\.?((25)|(50)|(5)|(75)|(0)|(00))?$/;
    const errors = {};
    if (value === "") {
      errors[name] = "Input cannot be empty.";
    } else if (pattern.test(value)) {
      errors[name] = "";
    } else {
      errors[name] =
        "Invalid input. Must be an integer or a float with digits after the decimal point being 25, 50, 75, 0, or 00.";
    }
    setFormErrors(errors);
  };
  let productionMainData = searchText.length > 0 ? getSearchResults(productionSheetData?.production, searchText) : productionSheetData?.production;

  const getDailySubmitID = productionSheetData?.production[0]?.dailySubmitID || productionSheetData?.dailySubmit?.id;

  const getSheetObj = {
    intStoreID: productionSheetData?.intStoreID | store?.intStoreID,
    dateSelected: productionSheetData?.dailySubmit?.dateSelected,
  };

  const unlockWithDailySubmitId = async (dailySubmitId) => {
    setLoading(true);
    try {
      if (!navigator.onLine) {
        throw new Error("No internet connection");
      }
      const unLockResp = await serviceapi.submitUnlockAPi(dailySubmitId);
      await getProductionSheet(getSheetObj);
      if (unLockResp?.statusCode && unLockResp?.statusCode === 200) {
        enqueueSnackbar("Unlocked Successfully", {
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "success",
        });
      } else {
        enqueueSnackbar(`Data error while Unlocking`, {
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        });
      }
    } catch (error) {
      try {
        const errorMsg = String(error?.message || error || 'Unknown error');
        const isNetworkError = !navigator.onLine || 
                               error?.code === 'ERR_NETWORK' || 
                               errorMsg.includes('Failed to fetch') ||
                               errorMsg.includes('Network') ||
                               errorMsg.includes('ECONNABORTED') ||
                               errorMsg.includes("Cannot destructure property 'status' of 'error.response'");
        enqueueSnackbar(isNetworkError ? "Network connection lost. Please check your internet connection and try again." : `Error while Unlocking: ${errorMsg}`, {
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        });
      } catch (e) {
        console.error('Error in unlock catch handler:', e);
        enqueueSnackbar("Error while Unlocking. Please try again.", {
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  }

  const unlockWithOutDailySubmitId = async (newObj) => {
    setLoading(true);
    try {
      if (!navigator.onLine) {
        throw new Error("No internet connection");
      }
      const unLockResponse = await serviceapi?.submitProductionSheetData(newObj);
      await getProductionSheet(getSheetObj);
      if (unLockResponse?.statusCode && unLockResponse?.statusCode === 200) {
        enqueueSnackbar("Unlocked Successfully", {
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "success",
        });
      } else {
        enqueueSnackbar(`Data error while Unlocking`, {
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        });
      }
    } catch (error) {
      try {
        const errorMsg = String(error?.message || error || 'Unknown error');
        const isNetworkError = !navigator.onLine || 
                               error?.code === 'ERR_NETWORK' || 
                               errorMsg.includes('Failed to fetch') ||
                               errorMsg.includes('Network') ||
                               errorMsg.includes('ECONNABORTED') ||
                               errorMsg.includes("Cannot destructure property 'status' of 'error.response'");
        enqueueSnackbar(isNetworkError ? "Network connection lost. Please check your internet connection and try again." : `Error while Unlocking: ${errorMsg}`, {
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        });
      } catch (e) {
        console.error('Error in unlock catch handler:', e);
        enqueueSnackbar("Error while Unlocking. Please try again.", {
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  }

  const submitUnlock = async () => {
    if (getDailySubmitID) {
      let dailySubmitId = {
        id: getDailySubmitID,
        isAdmin: !checkForCorporateUser(data?.profile?.role) ? true : false
      }
      unlockWithDailySubmitId(dailySubmitId)
    } else {
      let newObj = {
        dailySubmit: {
          dateSelected: productionSheetData?.dailySubmit?.dateSelected,
          status: 4,
          intStoreID: productionSheetData?.intStoreID | store?.intStoreID,
        },
        production: productionSheetData?.production || []
      }
      unlockWithOutDailySubmitId(newObj)
    }
  }

  const handleConfirmDialog = () => {
    setConfirmDialog({ open: !confirmDialog.open, selectedDate: '' });
    setProductionItem(defaultItem);
    changeDateConfirm(confirmDialog.selectedDate);
    setDraftMessage(false)
  }

  const handleCancelDialog = () => {
    setConfirmDialog({ open: false, selectedDate: '' });
  }

  const getTimeDifferenceInHoursForCorporateUsers = (date) => {
    const unlockDate = productionSheetData?.dailySubmit?.unlockedAt ?? productionSheetData?.dailySubmit?.unlockedAt
    const currentDate = dayjs()
    const givenDate = dayjs(date)
    const timeDifferenceInHrs = unlockDate ? currentDate.diff(unlockDate, 'hours') : currentDate.diff(givenDate, 'hours');
    return timeDifferenceInHrs;
  }

  const userUnlock = (date) => {
    if (productionSheetData?.dailySubmit?.status === 3 && getTimeDifferenceInHoursForCorporateUsers(date) <= UserPermisions['Franchisee']?.unlock)
      return true
    else
      return false
  }

  const adminUnlock = (date) => {
    if (getTimeDifferenceInHoursForCorporateUsers(date) <= UserPermisions['Franchisee']?.unlock)
      return true
    else
      return false
  }

  const canUnlock = (date) => {
    if (data?.profile?.role === 'Super Admin' || data?.profile?.role === 'Admin' || data?.profile?.role === 'Director(FC Lead)') {
      if (productionSheetData?.dailySubmit?.status === 3)
        return true
      else
        return !adminUnlock(date)
    } else if (data?.profile?.role === 'Franchise Consultant') {
      const currentDate = dayjs()
      const givenDate = dayjs(date)
      const timeDifferenceInHrs = currentDate.diff(givenDate, 'hours');
      if (productionSheetData?.dailySubmit?.status === 3 && timeDifferenceInHrs <= UserPermisions[data?.profile?.role]?.unlock)
        return true
      else {
        if (timeDifferenceInHrs <= UserPermisions[data?.profile?.role]?.unlock)
          return !adminUnlock(date)
        else
          return userUnlock(date)
      }
    } else {
      return userUnlock(date)
    }
  }

  const canSubmit = (date) => {
    const unlockDate = productionSheetData?.dailySubmit?.unlockedAt ?? productionSheetData?.dailySubmit?.unlockedAt
    const currentDate = dayjs()
    const givenDate = dayjs(date)
    const timeDifferenceInHrs = unlockDate ? currentDate.diff(unlockDate, 'hours') : currentDate.diff(givenDate, 'hours');
    if (productionSheetData?.dailySubmit?.status !== 3 && timeDifferenceInHrs <= UserPermisions[data?.profile?.role]?.unlock)
      return true
    else
      return false
  }

  const getTodayDate = () => {
    return dayjs()
  }

  const handleScroll = (direction = '') => {
    const productionSheetElement = document.getElementById('production-table-root');
    productionSheetElement.scrollTo({ top: direction === 'top' ? 0 : productionSheetElement.scrollHeight, behavior: 'smooth' });
  }

  return (
    <>
      <Dialog
        open={open}
        fullScreen={width < breakPointWidth ? true : false}
        onClose={handleClose}
        PaperProps={
          width > breakPointWidth ? {
            style: {
              minWidth: "60%",
              maxWidth: "65%",
              minHeight: "55%",
              maxHeight: "100%",
            },
          } : {}
        }
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle style={{ paddingTop: '0px', paddingBottom: '0px' }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>Recipe Card</div>
            <Button
              onClick={handleClose}
              style={{ borderColor: "slateblue" }}
            >
              <CloseTwoTone />
            </Button>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <img
            style={{ width: "100%" }}
            src={selectedImage}
            onLoad={imageOnLoad}
            onError={imageOnError}
            alt="recipe"
          />
        </DialogContent>
        <DialogActions>
        </DialogActions>
      </Dialog>
      <ConfirmationPopup openDialog={confirmDialog?.open} handleCancelDialog={handleCancelDialog} handleConfirmDialog={handleConfirmDialog} />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading || flavorLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <div style={{ height: "calc(100vh - 50px)" }} className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-7">
        <Popper id={'banner-message'} open={showBanner && userOnboarding?.tourStatus?.productionSheets} style={{ width: '100%', zIndex: 300 }} >
          <Box sx={{ p: 1.5, bgcolor: '#F8FFE2' }} className="flex items-center justify-between mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {`Note: If you are unable to unlock a Production Sheet, contact your Franchise Consultant for assistance.`}
            <IconButton onClick={() => setShowBanner(false)} ><XCircleIcon className="h-5 w-5 text-gray-400" /></IconButton>
          </Box>
        </Popper>
        <header className="pt-6 pb-4 mt-12">
          <div className="flex justify-between items-center flex-wrap mt-1 gap-y-2 gap-x-3">
            <div className="flex flex-row justify-between flex-grow">
              <h1 className="text-2xl font-display font-medium leading-tight text-secondary">
                Daily Production Sheets
              </h1>
              {(!checkForCorporateUser(data?.profile?.role) && productionSheetData?.dailySubmit?.id && process.env?.REACT_APP_ENVIRONMENT === "TRAIN") ?
                <Button variant="outlined" color="error" onClick={() => { setClearDataConfirmation(true) }}>Clear Data</Button> : null}
              {openClearPSDataPopup && <ConfirmationPopup openDialog={openClearPSDataPopup} handleCancelDialog={handleClearDataCancellation} handleConfirmDialog={handleClearDataConfirmation} />}
            </div>
            <div className="flex justify-end sm:flex-grow md:flex-grow lg:flex-none items-center">
              {/* Current Weather info */}
              {/* {currentWeather?.temp_f && <Card sx={{ minWidth: 120, height: 36, background: '#d6e1ed' }}>
                <CardContent style={{ padding: '3px 10px', position: 'relative' }}>
                  <span className="text-[8px] absolute bottom-0 right-1">Current Weather</span>
                  <div className="flex flex-row justify-between items-center text-sm" variant="body2">
                    <span>{`${parseInt(currentWeather?.temp_f)}°F`} <span style={{ fontSize: 12 }}>| {currentWeather?.condition?.text}</span></span>  {currentWeather?.condition?.icon && <img style={{ height: '30px' }} src={currentWeather?.condition?.icon} />}
                  </div>
                </CardContent>
              </Card>} */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  slotProps={{
                    textField: {
                      size: "small",
                      error: false,
                    },
                  }}
                  sx={{
                    width: 180, marginLeft: '14px', '&>div': { height: 37.33, backgroundColor: '#FFF', borderRadius: '5px' }, '& input': { background: 'none' }
                  }}
                  adapterLocale="en"
                  format="MM/DD/YYYY"
                  value={dayjs(productionSheetData?.dailySubmit?.dateSelected)}
                  onChange={(newValue) => changeDate(newValue)}
                  // minDate={minSelectableDate}
                  maxDate={getTodayDate()}
                  className="date-picker-ps-tour-selector"
                />
              </LocalizationProvider>
              <IconButton title="Scroll to bottom" className="bg-white p-1 h-fit mx-3 scroll-down-ps-tour-selector" onClick={(e) => handleScroll('bottom')} ><KeyboardArrowDown /></IconButton>
              <IconButton title="Scroll to top" className="bg-white p-1 h-fit scroll-up-ps-tour-selector" onClick={(e) => handleScroll('top')} ><KeyboardArrowUp /></IconButton>
            </div>
          </div>
          <p className="text-xs mb-[-10px]">Note*:- Decimal Conversions - 0.125 = 1/8; 0.25 = 1/4; 0.375 = 3/8; 0.5 = 1/2; 0.625 = 5/8; 0.75 = 3/4; 0.875 = 7/8; <Tooltip arrow title={decimalTooltipText}><Info fontSize="small" color="disabled" /></Tooltip></p>
        </header>
        {store && (
          <>
            <main className="production-sheet">
              <div>
                <form className="bg-white px-4 py-4 rounded ps-form-tour-selector" onSubmit={handleAdd} disabled={checkForResubmit()?.classValue}>
                  <Grid container spacing={{ xs: 1, md: 2 }} rowGap={1} className="  ">
                    {/* <Grid item xs={12} md className="flex justify-between">
                      <FormControl sx={{ marginRight: '16px' }} fullWidth size="small">
                        <TextField
                          placeholder="Search by Flavor name"
                          value={searchText}
                          onChange={handleSearchChange}
                          InputProps={{ endAdornment: <>{searchText && <IconButton title="Clear" onClick={() => setSearchText('')} size="small"><XMarkIcon className="h-5 w-5 text-gray-400" /></IconButton>}<MagnifyingGlassIcon className="h-5 w-5 text-gray-400" /></> }}
                        />
                      </FormControl>
                      <Divider orientation="vertical" />
                    </Grid> */}
                    <Grid item xs={2} >
                      <FormControl fullWidth size="small" className="ps-flavor-type-tour-selector">
                        {/* sx={{ width: 115 }} */}
                        {/* <InputLabel id="filterBy">Filter by</InputLabel> */}
                        <Select
                          labelId="filterBy"
                          id="filterBy"
                          // sx={{ height: 37 }}
                          // defaultValue={'recent'}
                          value={filterBy}
                          onChange={(e) => { setProductionItem({ ...defaultItem }); setFilterBy(e.target.value); }}
                        >
                          {ProductionSheetFilters?.map((i) => (
                            <MenuItem
                              key={i?.value}
                              value={i?.value}
                            >
                              {i?.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                      <FormControl fullWidth size="small" className="ps-flavor-category-tour-selector">
                        <InputLabel id="intProductTypeID">Category</InputLabel>
                        <Select
                          labelId="intProductTypeID"
                          id="intProductTypeID"
                          value={productionItem?.intProductTypeID || null}
                          label="Category"
                          onChange={(e) =>
                            handleChange(e.target.value, "intProductTypeID")
                          }
                          error={!!formErrors?.intProductTypeID}
                        >
                          {ProductionTypeCategoryList?.map((i) => (
                            <MenuItem
                              key={i?.intProductTypeID}
                              value={i?.intProductTypeID}
                            >
                              {i?.product_type_name}
                            </MenuItem>
                          ))}
                        </Select>
                        {formErrors.intProductTypeID && (
                          <FormHelperText error>
                            {formErrors.intProductTypeID}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={2} md={3} maxWidth={270}> {/* flexGrow={1} */}
                      <FormControl fullWidth size="small" className="ps-flavor-tour-selector">
                        <InputLabel id="intFlavorID">Flavor</InputLabel>
                        <Select
                          labelId="intFlavorID"
                          id="intFlavorID"
                          value={productionItem?.intFlavorID || null}
                          label="Flavor"
                          onChange={(e) =>
                            handleChange(e.target.value, "intFlavorID")
                          }
                          error={!!formErrors?.intFlavorID}
                        >
                          {flavorsList?.map((i) => (
                            <MenuItem key={i?.intFlavorID} value={i?.intFlavorID}>
                              {i?.flavor_name}
                            </MenuItem>
                          ))}
                        </Select>
                        {formErrors.intFlavorID && (
                          <FormHelperText error>
                            {formErrors.intFlavorID}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                      <FormControl fullWidth size="small" className="ps-container-tour-selector">
                        <InputLabel id="containerID">Container</InputLabel>
                        <Select
                          labelId="containerID"
                          id="containerID"
                          value={productionItem?.containerID || null}
                          renderValue={(value) => ContainerList.find(c => c.id === value)?.name?.split(" ").shift()}
                          label="Container"
                          onChange={(e) =>
                            handleChange(e.target.value, "containerID")
                          }
                          error={!!formErrors?.containerID}
                        >
                          {ContainerList?.map((i) => {
                            const recipeCard = flavorsList.length > 0 && flavorsList?.find((i) => i.intFlavorID === productionItem?.intFlavorID)?.[i.id === 1 ? 'quarterContainer' : i.id === 2 ? 'halfContainer' : i.id === 3 ? 'singleContainer' : 'doubleContainer'];
                            return (
                              <MenuItem key={i?.id} value={i?.id} disabled={!recipeCard}>
                                <p className="mr-3">{i?.name?.split(" ").shift()}</p>
                                {productionItem?.intFlavorID !== 0 && <IconButton
                                  title="Recipe Card"
                                  disabled={!recipeCard}
                                  sx={{ width: 20, height: 20, padding: 0, margin: "0 5px 0 auto", opacity: !recipeCard && .5 }}
                                  onClick={(e) => { e.stopPropagation(); showImagePDF(recipeCard); }}
                                >
                                  <img className="image" alt="no image"
                                    src="../../assets/icons/recipe-card-icon.png"
                                  />
                                </IconButton>}
                              </MenuItem>
                            )
                          })}
                        </Select>
                        {formErrors.containerID && (
                          <FormHelperText error>
                            {formErrors.containerID}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={1}>
                      <FormControl
                        sx={{ minWidth: 75, backgroundColor: "initial" }}
                        size="small"
                      >
                        <Button sx={{ height: 40 }} type="submit" variant="outlined" disabled={(!checkForCorporateUser(data?.profile?.role) || UserPermisions[data?.profile?.role]?.submit && !canSubmit(date))}>
                          {productionItem?.flavor ? "Update" : "Add"}
                        </Button>
                      </FormControl>
                    </Grid>
                  </Grid>
                </form>
              </div>
              <div id="production-table-root" className={`production-data mt-2 rounded ps-data-tour-selector ${userOnboarding?.tourStatus?.productionSheets && '!overflow-auto'}`} style={{ maxWidth: hasReportAccess && `calc(100vw - ${drawerOpen ? '256px' : '106px'})` }}>
                <Table sx={{ minWidth: 650, position: 'relative' }} aria-label="simple table">
                  <CategoryCard
                    dataItem={productionMainData}
                    handleEdit={handleEdit}
                    handleDelete={handleDelete}
                    showImagePDF={showImagePDF}
                    checkForResubmit={checkForResubmit}
                    getContainerValue={getContainerValue}
                    handleUpdate={(...args) => handleUpdate(...args)}
                    handleBulkUpdate={(...args) => handleBulkUpdate(...args)}
                    selectedDate={date}
                    canSubmit={canSubmit}
                    checkForCorporateUser={checkForCorporateUser}
                    calculateValues={calculateValues}
                    setIsEditing={setIsEditing}
                    sortBy={sortBy} setSortBy={setSortBy}
                    bulkEdit={bulkEdit}
                  />
                </Table>
              </div>
            </main>
            <ClickAwayListener onClickAway={() => setToggleFormOpen(false)} >
              <div style={{ zIndex: 300 }} className="absolute bottom-0 left-0 right-0 footer-main-div">
                <ToggleableContent
                  productionSheetData={productionSheetData}
                  data={data}
                  formChange={formChange}
                  formErrors={formErrors}
                  toggleFormOpen={toggleFormOpen}
                  setToggleFormOpen={setToggleFormOpen}
                  afternoonWeatherList={afternoonWeatherList}
                  eveningWeatherList={eveningWeatherList}
                />
                <div className="bg-white">
                  <div className="p-2 px-8 flex justify-between items-center py-2 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex gap-2">
                      <div className="font-bold">Total Summary:</div>
                      <div className="flex gap-3">
                        <span>
                          Start: {productionSheetData?.dailySubmit?.start}
                        </span>
                        <span>
                          Total: {productionSheetData?.dailySubmit?.total}
                        </span>
                        <span>Made: {productionSheetData?.dailySubmit?.made}</span>
                        <span>
                          Throw: {productionSheetData?.dailySubmit?.throw}
                        </span>
                        <span>Left: {productionSheetData?.dailySubmit?.leftOver}</span>
                        <span>Sold: {productionSheetData?.dailySubmit?.sold}</span>
                      </div>
                    </div>
                    <div className="flex justify-between gap-4 ps-status-btn-tour-selector">
                      {(UserPermisions[data?.profile?.role]?.submit && canSubmit(date)) && productionSheetData?.production?.length > 0 &&
                        <Button type="button" variant="outlined" color="success" disabled={isEditing} sx={{ color: 'green', borderColor: 'green' }} onClick={() => { setIsEditing(true); setBulkEdit(true); }} className="ps-edit-data-tour-selector">
                          Edit
                        </Button>
                      }
                      {(canUnlock(date)) && <div>
                        <FormControl
                          sx={{ minWidth: 78, textDecoration: "underline" }}
                          size="small"
                        >
                          <Button
                            type="submit"
                            variant="text"
                            onClick={submitUnlock}
                            style={{ textDecoration: "underline" }}
                          >
                            Un-lock
                          </Button>
                        </FormControl>
                      </div>
                      }
                      {
                        (!checkForCorporateUser(data?.profile?.role) && !canUnlock(date)) && <div>
                          <FormControl
                            sx={{ minWidth: 78, }}
                            size="small"
                          >
                            <Button
                              variant="text"
                              color="success"
                              style={{ color: "#fff", background: '#b4b4b4' }}
                            >
                              {data?.profile?.role === 'Franchise Consultant' && ((!adminUnlock(date)) ? <>Locked</> : ((productionSheetData?.dailySubmit?.status === 2 || productionSheetData?.dailySubmit?.status === 4) ? <>In Progress</> : <>Not Submitted</>))}
                              {data?.profile?.role !== 'Franchise Consultant' && ((productionSheetData?.dailySubmit?.status === 2 || productionSheetData?.dailySubmit?.status === 4) ? <>In Progress</> : <>Not Submitted</>)}
                            </Button>
                          </FormControl>
                        </div>
                      }
                      <div className="flex gap-4">
                        {((UserPermisions[data?.profile?.role]?.submit && canSubmit(date)) && ((productionSheetData?.production?.length > 0 && !productionSheetData?.production?.some((flv) => flv?.errors && Object.keys(flv?.errors)?.length !== 0)) ?
                          <>
                            <Button
                              type="submit"
                              variant="text"
                              color="success"
                              onClick={() => submitProductionData('save')}
                              style={{ color: "#fff", background: 'green' }}
                              className="ps-save-data-tour-selector"
                            >
                              Save
                            </Button>
                            <Button
                              type="submit"
                              variant="contained"
                              color="success"
                              onClick={() => submitProductionData('submit')}
                              style={{ background: 'green' }}
                              className="ps-submit-data-tour-selector"
                            >
                              Submit
                            </Button>
                          </>
                          :
                          <>
                            <Button
                              style={{
                                color: "#898989", borderRadius: '4px',
                                border: '2px solid #898989'
                              }}
                              type="submit"
                              variant="text"
                              disabled>
                              Save
                            </Button>
                            <Button
                              style={{ color: "#fff", padding: '7px 16px' }}
                              type="submit"
                              variant="contained"
                              disabled>
                              Submit
                            </Button>
                          </>
                        ))
                        } {
                          (UserPermisions[data?.profile?.role]?.submit && !canSubmit(date)) &&
                          <Button
                            style={{
                              color: "#898989", borderRadius: '4px',
                              border: '2px solid #898989'
                            }}
                            type="submit"
                            variant="text"
                            disabled>
                            Locked
                          </Button>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ClickAwayListener>
          </>
        )}
        {!store && (
          <div
            className=""
            style={{
              border: "1px solid #ccc",
              padding: "10px",
              // margin: "10px",
              backgroundColor: "#FFF",
              color: "#000",
            }}
          >
            Please select a store!
          </div>
        )}
      </div>
    </>
  );
};

export default ProductionSheet;